import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';
import {
  Tournament,
  TournamentForm,
  TournamentFilters,
  GetTournamentsResponse,
  TournamentAnalytics,
  CompleteUser,
} from '../../types';

interface TournamentsState {
  tournaments: Tournament[];
  selectedTournament: Tournament | null;
  tournamentPlayers: CompleteUser[];
  tournamentAnalytics: TournamentAnalytics | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics: {
    totalTournaments: number;
    activeTournaments: number;
    totalPrizePool: number;
  };
  filters: TournamentFilters;
  loading: boolean;
  error: string | null;
}

const initialState: TournamentsState = {
  tournaments: [],
  selectedTournament: null,
  tournamentPlayers: [],
  tournamentAnalytics: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  statistics: {
    totalTournaments: 0,
    activeTournaments: 0,
    totalPrizePool: 0,
  },
  filters: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchTournaments = createAsyncThunk(
  'tournaments/fetchTournaments',
  async ({ page = 1, limit = 20, filters }: { page?: number; limit?: number; filters?: TournamentFilters }) => {
    const response = await apiService.getTournaments(page, limit, filters);
    return response;
  }
);

export const fetchTournament = createAsyncThunk(
  'tournaments/fetchTournament',
  async (tournamentId: string) => {
    const response = await apiService.getTournament(tournamentId);
    return response;
  }
);

export const createTournament = createAsyncThunk(
  'tournaments/createTournament',
  async (tournamentData: TournamentForm) => {
    const response = await apiService.createTournament(tournamentData);
    return response;
  }
);

export const updateTournament = createAsyncThunk(
  'tournaments/updateTournament',
  async ({ tournamentId, tournamentData }: { tournamentId: string; tournamentData: Partial<TournamentForm> }) => {
    const response = await apiService.updateTournament(tournamentId, tournamentData);
    return response;
  }
);

export const deleteTournament = createAsyncThunk(
  'tournaments/deleteTournament',
  async (tournamentId: string) => {
    const response = await apiService.deleteTournament(tournamentId);
    return { ...response, tournamentId };
  }
);

export const forceDeleteTournament = createAsyncThunk(
  'tournaments/forceDeleteTournament',
  async (tournamentId: string) => {
    const response = await apiService.forceDeleteTournament(tournamentId);
    return { ...response, tournamentId };
  }
);

export const forceStartTournament = createAsyncThunk(
  'tournaments/forceStartTournament',
  async (tournamentId: string) => {
    const response = await apiService.forceStartTournament(tournamentId);
    return response;
  }
);

export const cancelTournament = createAsyncThunk(
  'tournaments/cancelTournament',
  async (tournamentId: string) => {
    const response = await apiService.cancelTournament(tournamentId);
    return response;
  }
);

export const fetchTournamentAnalytics = createAsyncThunk(
  'tournaments/fetchTournamentAnalytics',
  async (tournamentId: string) => {
    const response = await apiService.getTournamentAnalytics(tournamentId);
    return response;
  }
);

export const fetchTournamentPlayers = createAsyncThunk(
  'tournaments/fetchTournamentPlayers',
  async (tournamentId: string) => {
    const response = await apiService.getTournamentPlayers(tournamentId);
    return response;
  }
);

export const addPlayerToTournament = createAsyncThunk(
  'tournaments/addPlayerToTournament',
  async ({ tournamentId, userId }: { tournamentId: string; userId: string }) => {
    const response = await apiService.addPlayerToTournament(tournamentId, userId);
    return response;
  }
);

export const removePlayerFromTournament = createAsyncThunk(
  'tournaments/removePlayerFromTournament',
  async ({ tournamentId, userId }: { tournamentId: string; userId: string }) => {
    const response = await apiService.removePlayerFromTournament(tournamentId, userId);
    return response;
  }
);

export const generateTournamentCards = createAsyncThunk(
  'tournaments/generateTournamentCards',
  async (tournamentId: string) => {
    const response = await apiService.generateTournamentCards(tournamentId);
    return { ...response, tournamentId };
  }
);

const tournamentsSlice = createSlice({
  name: 'tournaments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedTournament: (state, action: PayloadAction<Tournament | null>) => {
      state.selectedTournament = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setFilters: (state, action: PayloadAction<TournamentFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    clearTournamentAnalytics: (state) => {
      state.tournamentAnalytics = null;
    },
    clearTournamentPlayers: (state) => {
      state.tournamentPlayers = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tournaments
      .addCase(fetchTournaments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTournaments.fulfilled, (state, action) => {
        state.loading = false;
        
        // Handle the response data safely
        if (action.payload) {
          state.tournaments = action.payload.tournaments || [];
          
          if (action.payload.pagination) {
            state.pagination = {
              page: action.payload.pagination.page || 1,
              limit: action.payload.pagination.limit || 20,
              total: action.payload.pagination.total || 0,
              totalPages: action.payload.pagination.pages || 0,
            };
          }
          
          if (action.payload.statistics) {
            state.statistics = {
              totalTournaments: action.payload.statistics.totalTournaments || 0,
              activeTournaments: action.payload.statistics.activeTournaments || 0,
              totalPrizePool: action.payload.statistics.totalPrizePool || 0,
            };
          }
        } else {
          // Fallback if payload is undefined
          state.tournaments = [];
          state.pagination = {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0,
          };
          state.statistics = {
            totalTournaments: 0,
            activeTournaments: 0,
            totalPrizePool: 0,
          };
        }
      })
      .addCase(fetchTournaments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tournaments';
      })
      // Fetch single tournament
      .addCase(fetchTournament.fulfilled, (state, action) => {
        state.selectedTournament = action.payload;
      })
      // Create tournament
      .addCase(createTournament.fulfilled, (state, action) => {
        state.tournaments.unshift(action.payload);
        state.statistics.totalTournaments += 1;
      })
      // Update tournament
      .addCase(updateTournament.fulfilled, (state, action) => {
        const index = state.tournaments.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tournaments[index] = action.payload;
        }
        if (state.selectedTournament?.id === action.payload.id) {
          state.selectedTournament = action.payload;
        }
      })
      // Delete tournament
      .addCase(deleteTournament.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.tournaments = state.tournaments.filter(t => t.id !== action.payload.tournamentId);
          if (state.selectedTournament?.id === action.payload.tournamentId) {
            state.selectedTournament = null;
          }
          state.statistics.totalTournaments -= 1;
        }
      })
      // Force delete tournament
      .addCase(forceDeleteTournament.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.tournaments = state.tournaments.filter(t => t.id !== action.payload.tournamentId);
          if (state.selectedTournament?.id === action.payload.tournamentId) {
            state.selectedTournament = null;
          }
          state.statistics.totalTournaments -= 1;
        }
      })
      // Force start tournament
      .addCase(forceStartTournament.fulfilled, (state, action) => {
        const index = state.tournaments.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tournaments[index] = action.payload;
        }
        if (state.selectedTournament?.id === action.payload.id) {
          state.selectedTournament = action.payload;
        }
      })
      // Cancel tournament
      .addCase(cancelTournament.fulfilled, (state, action) => {
        const index = state.tournaments.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tournaments[index] = action.payload;
        }
        if (state.selectedTournament?.id === action.payload.id) {
          state.selectedTournament = action.payload;
        }
      })
      // Fetch tournament analytics
      .addCase(fetchTournamentAnalytics.fulfilled, (state, action) => {
        state.tournamentAnalytics = action.payload;
      })
      // Fetch tournament players
      .addCase(fetchTournamentPlayers.fulfilled, (state, action) => {
        state.tournamentPlayers = action.payload;
      })
      // Add player to tournament
      .addCase(addPlayerToTournament.fulfilled, (state, action) => {
        const index = state.tournaments.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tournaments[index] = action.payload;
        }
        if (state.selectedTournament?.id === action.payload.id) {
          state.selectedTournament = action.payload;
        }
      })
      // Remove player from tournament
      .addCase(removePlayerFromTournament.fulfilled, (state, action) => {
        const index = state.tournaments.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tournaments[index] = action.payload;
        }
        if (state.selectedTournament?.id === action.payload.id) {
          state.selectedTournament = action.payload;
        }
      });
  },
});

export const {
  clearError,
  setSelectedTournament,
  setPage,
  setFilters,
  clearFilters,
  clearTournamentAnalytics,
  clearTournamentPlayers,
} = tournamentsSlice.actions;

export default tournamentsSlice.reducer; 